import { useRef, useState, type ReactNode } from 'react';
import { Upload } from 'lucide-react';

/**
 * Drag a file here, or choose one (owner, 2026-09-21: "All files, add drag and drop + choose
 * file"). Every file control in the app goes through this, so they all behave alike.
 *
 * No `capture` attribute: on a phone that forces the camera and hides "choose from files".
 * Without it the phone offers both - take a photo now, or pick a scan already on the device.
 */
export function DropZone({
  accept,
  onFile,
  disabled,
  busy,
  label = 'Drag a file here, or',
  buttonLabel = 'Choose file',
  hint,
  children,
  multiple,
}: {
  accept?: string;
  /** Called once per file; with `multiple`, once for each file chosen or dropped. */
  onFile: (file: File) => void;
  multiple?: boolean;
  disabled?: boolean;
  busy?: boolean;
  label?: string;
  buttonLabel?: string;
  hint?: string;
  children?: ReactNode;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const off = disabled || busy;

  function take(files: FileList | null | undefined) {
    if (!files || off) return;
    for (const file of multiple ? Array.from(files) : Array.from(files).slice(0, 1)) onFile(file);
  }

  return (
    <div
      className={`drop-zone${over ? ' over' : ''}${off ? ' off' : ''}`}
      onDragLeave={() => setOver(false)}
      onDragOver={(event) => { event.preventDefault(); if (!off) setOver(true); }}
      onDrop={(event) => { event.preventDefault(); setOver(false); take(event.dataTransfer.files); }}
    >
      {children}
      <div className="drop-zone-prompt">
        <Upload size={16} />
        <span>{busy ? 'Uploading...' : label}</span>
        <button disabled={off} onClick={() => input.current?.click()} type="button">{buttonLabel}</button>
      </div>
      {hint && <small className="muted">{hint}</small>}
      <input accept={accept} hidden multiple={multiple} onChange={(event) => { take(event.target.files); event.target.value = ''; }}
             ref={input} type="file" />
    </div>
  );
}
